/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        CAT Translation Studio Project DTOs (Task 8.1.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';
import { TranscreationTone } from '../enums';

/** BCP-47 style language code (e.g. "en", "vi", "zh-CN"). */
const LanguageCodeSchema = z
  .string()
  .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Invalid language code')
  .openapi({ description: 'BCP-47 language code', example: 'vi' });

export const TranslationDomainSchema = z.enum(['general', 'legal', 'medical', 'tech', 'finance']).openapi({
  description: 'Subject domain used to steer terminology',
  example: 'general',
});

export const TranslationProjectStatusSchema = z.enum(['draft', 'in_progress', 'completed']).openapi({
  description: 'Project lifecycle status',
  example: 'draft',
});

/** Maximum inline source text length accepted at project creation (characters). */
export const TRANSLATION_MAX_SOURCE_CHARS = 500_000;

/**
 * Create one or more translation projects (one project per target language).
 */
export const CreateTranslationSchema = z
  .object({
    title: z.string().trim().min(1).max(200).openapi({ example: 'Product manual v2' }),
    description: z.string().max(2000).optional().openapi({ example: 'User manual for the new device' }),
    sourceLang: LanguageCodeSchema,
    targetLangs: z.array(LanguageCodeSchema).min(1).max(20).openapi({ example: ['vi', 'fr'] }),
    sourceText: z.string().min(1).max(TRANSLATION_MAX_SOURCE_CHARS).optional().openapi({
      description: 'Inline source text (mutually exclusive with fileStorageKey)',
    }),
    fileStorageKey: z.string().min(1).max(512).optional().openapi({
      description: 'R2 object key of an uploaded source document',
      example: 'translations/ws_01/source.docx',
    }),
    domain: TranslationDomainSchema.default('general'),
    tone: z.string().trim().min(1).max(50).optional().openapi({ example: 'formal' }),
    glossaryId: z.string().min(1).max(100).optional().openapi({ description: 'Glossary to apply' }),
  })
  .superRefine((value, ctx) => {
    if (!value.sourceText && !value.fileStorageKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Either sourceText or fileStorageKey is required',
        path: ['sourceText'],
      });
    }
    if (value.sourceText && value.fileStorageKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide only one of sourceText or fileStorageKey',
        path: ['fileStorageKey'],
      });
    }
    if (value.targetLangs.includes(value.sourceLang)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'targetLangs must not contain the source language',
        path: ['targetLangs'],
      });
    }
    if (new Set(value.targetLangs).size !== value.targetLangs.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'targetLangs must be unique',
        path: ['targetLangs'],
      });
    }
  })
  .openapi('CreateTranslation');

export type CreateTranslationDto = z.infer<typeof CreateTranslationSchema>;

/**
 * Compact translation project view.
 */
export const TranslationProjectResponseSchema = z
  .object({
    id: z.string().openapi({ example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    workspaceId: z.string().openapi({ example: 'ws_01' }),
    userId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    sourceLang: z.string().openapi({ example: 'en' }),
    targetLang: z.string().openapi({ example: 'vi' }),
    domain: z.string().openapi({ example: 'general' }),
    tone: z.string().nullable(),
    glossaryId: z.string().nullable(),
    status: TranslationProjectStatusSchema,
    wordCount: z.number().int().nonnegative(),
    charCount: z.number().int().nonnegative(),
    totalChunks: z.number().int().nonnegative(),
    translatedChunks: z.number().int().nonnegative(),
    progressPercent: z.number().min(0).max(100).openapi({ description: 'translatedChunks / totalChunks * 100' }),
    createdAt: z.string().openapi({ example: '2026-10-22T03:00:00.000Z' }),
  })
  .openapi('TranslationProjectResponse');

export type TranslationProjectDto = z.infer<typeof TranslationProjectResponseSchema>;

export const CreateTranslationResponseSchema = z
  .object({
    projects: z.array(TranslationProjectResponseSchema).openapi({ description: 'One project per target language' }),
  })
  .openapi('CreateTranslationResponse');

export type CreateTranslationResponseDto = z.infer<typeof CreateTranslationResponseSchema>;

/**
 * Query parameters for listing translation projects.
 */
export const ListTranslationsQuerySchema = z
  .object({
    status: TranslationProjectStatusSchema.optional(),
    domain: TranslationDomainSchema.optional(),
    sourceLang: z.string().min(1).optional().openapi({ example: 'en' }),
    targetLang: z.string().min(1).optional().openapi({ example: 'vi' }),
    search: z.string().trim().min(1).max(100).optional().openapi({ description: 'Title substring search' }),
    sortBy: z.enum(['createdAt', 'title', 'progress']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .openapi('ListTranslationsQuery');

export type ListTranslationsQueryDto = z.infer<typeof ListTranslationsQuerySchema>;

export const ListTranslationsResponseSchema = z
  .object({
    items: z.array(TranslationProjectResponseSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      total: z.number().int(),
      totalPages: z.number().int(),
    }),
  })
  .openapi('ListTranslationsResponse');

export type ListTranslationsResponseDto = z.infer<typeof ListTranslationsResponseSchema>;

/**
 * Project detail including per-status chunk counters.
 */
export const TranslationDetailSchema = TranslationProjectResponseSchema.extend({
  sourceFileKey: z.string().nullable(),
  hasInlineSource: z.boolean().openapi({ description: 'True when source text was supplied inline' }),
  chunkCounts: z
    .object({
      untranslated: z.number().int().nonnegative(),
      draft: z.number().int().nonnegative(),
      reviewed: z.number().int().nonnegative(),
      approved: z.number().int().nonnegative().openapi({
        description: 'Reserved: approval state is not persisted yet, always 0 until Task 8.1.3',
      }),
    })
    .openapi({ description: 'Chunk counts per workflow status' }),
}).openapi('TranslationDetail');

export type TranslationDetailDto = z.infer<typeof TranslationDetailSchema>;

/**
 * Partial project update.
 */
export const UpdateTranslationSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    domain: TranslationDomainSchema.optional(),
    tone: z.string().trim().min(1).max(50).nullable().optional(),
    glossaryId: z.string().min(1).max(100).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' })
  .openapi('UpdateTranslation');

export type UpdateTranslationDto = z.infer<typeof UpdateTranslationSchema>;

export const DeleteTranslationResponseSchema = z
  .object({
    id: z.string(),
    deleted: z.boolean(),
    deletedChunks: z.number().int().nonnegative().openapi({ description: 'Number of chunks removed with the project' }),
  })
  .openapi('DeleteTranslationResponse');

export type DeleteTranslationResponseDto = z.infer<typeof DeleteTranslationResponseSchema>;

// ============================================================================
// CHUNKS & INGESTION DTOs (Task 8.1.3)
// ============================================================================

export const TranslationChunkStatusSchema = z
  .enum(['untranslated', 'draft', 'reviewed', 'approved'])
  .openapi({ description: 'Review state of a translation segment', example: 'untranslated' });

export type TranslationChunkStatus = z.infer<typeof TranslationChunkStatusSchema>;

export const TranslationMatchTypeSchema = z
  .enum(['exact', 'fuzzy', 'mt', 'manual'])
  .nullable()
  .openapi({ description: 'Source of target translation match', example: 'exact' });

export type TranslationMatchType = z.infer<typeof TranslationMatchTypeSchema>;

export const TranslationChunkResponseSchema = z
  .object({
    id: z.string().openapi({ example: 'c101c101-0000-4000-8000-000000000001' }),
    translationId: z.string().openapi({ example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    sequenceOrder: z.number().int().nonnegative().openapi({ description: '0-based sequential index of the chunk' }),
    chunkIndex: z.number().int().nonnegative().openapi({ description: 'Alias for sequenceOrder' }),
    sourceText: z.string().openapi({ example: 'Welcome to Sophia Platform.' }),
    sourceHash: z.string().nullable().openapi({ description: 'SHA-256 hash of normalized source string' }),
    targetText: z.string().nullable().openapi({ example: 'Chào mừng bạn đến với Sophia Platform.' }),
    status: TranslationChunkStatusSchema,
    matchType: TranslationMatchTypeSchema,
    similarityScore: z.number().int().min(0).max(100).openapi({ description: 'Similarity percentage score (0-100)' }),
    isReviewed: z.boolean().openapi({ description: 'Reviewed flag' }),
    tmMatchPercent: z.number().int().min(0).max(100).openapi({ description: 'TM match percentage' }),
    qaWarnings: z.array(z.string()).default([]).openapi({ description: 'QA linter warnings detected in target text' }),
    propagatedCount: z.number().int().nonnegative().optional().openapi({ description: 'Number of identical segments auto-propagated' }),
  })
  .openapi('TranslationChunkResponse');

export type TranslationChunkDto = z.infer<typeof TranslationChunkResponseSchema>;
export const ChunkItemSchema = TranslationChunkResponseSchema;
export type ChunkItemDto = TranslationChunkDto;

export const IngestTranslationSchema = z
  .object({
    sourceText: z.string().min(1).max(TRANSLATION_MAX_SOURCE_CHARS).optional().openapi({
      description: 'Raw source text to segment and ingest into D1',
    }),
    segments: z.array(z.string().min(1)).min(1).optional().openapi({
      description: 'Pre-segmented sentence array (bypasses automatic sentence splitter)',
    }),
    batchSize: z.coerce.number().int().min(1).max(100).default(50).optional().openapi({
      description: 'Chunk batch size per atomic transaction (1-100, default: 50)',
      example: 50,
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.sourceText && (!data.segments || data.segments.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Either sourceText or segments array must be provided',
        path: ['sourceText'],
      });
    }
  })
  .openapi('IngestTranslation');

export type IngestTranslationDto = z.infer<typeof IngestTranslationSchema>;

export const IngestTranslationResponseSchema = z
  .object({
    translationId: z.string().openapi({ example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    totalChunks: z.number().int().nonnegative().openapi({ description: 'Total number of ingested chunks' }),
    totalWords: z.number().int().nonnegative().openapi({ description: 'Total word count of ingested segments' }),
    totalChars: z.number().int().nonnegative().openapi({ description: 'Total character count of ingested segments' }),
    batchesExecuted: z.number().int().nonnegative().openapi({ description: 'Number of atomic D1 batch transactions' }),
    status: TranslationProjectStatusSchema,
  })
  .openapi('IngestTranslationResponse');

export type IngestTranslationResponseDto = z.infer<typeof IngestTranslationResponseSchema>;

export const ListChunksQuerySchema = z
  .object({
    status: TranslationChunkStatusSchema.optional().openapi({ description: 'Filter by segment status' }),
    isReviewed: z.coerce.boolean().optional().openapi({ description: 'Filter by reviewed status' }),
    matchType: z.enum(['exact', 'fuzzy', 'mt', 'manual']).optional().openapi({ description: 'Filter by provenance match type' }),
    searchQuery: z.string().trim().optional().openapi({ description: 'Search text in source or target' }),
    hasQaWarnings: z.coerce.boolean().optional().openapi({ description: 'Filter by presence of QA warnings' }),
    page: z.coerce.number().int().min(1).default(1).optional().openapi({ example: 1 }),
    limit: z.coerce.number().int().min(1).max(500).default(50).optional().openapi({ example: 50 }),
  })
  .openapi('ListChunksQuery');

export type ListChunksQueryDto = z.infer<typeof ListChunksQuerySchema>;

export const ListChunksResponseSchema = z
  .object({
    items: z.array(TranslationChunkResponseSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      total: z.number().int(),
      totalPages: z.number().int(),
    }),
    counts: z.object({
      total: z.number().int().nonnegative(),
      untranslated: z.number().int().nonnegative(),
      draft: z.number().int().nonnegative(),
      reviewed: z.number().int().nonnegative(),
    }).optional().openapi({ description: 'Segment status breakdown' }),
  })
  .openapi('ListChunksResponse');

export type ListChunksResponseDto = z.infer<typeof ListChunksResponseSchema>;

// ============================================================================
// AUTO-TRANSLATE DTOs (Task 8.3.1)
// ============================================================================

export const AutoTranslateProjectRequestSchema = z
  .object({
    provider: z
      .string()
      .trim()
      .default('openai')
      .optional()
      .openapi({ description: 'AI Provider (openai, anthropic, gemini, deepseek)', example: 'openai' }),
    model: z
      .string()
      .trim()
      .default('gpt-4o-mini')
      .optional()
      .openapi({ description: 'LLM model ID to use for translation', example: 'gpt-4o-mini' }),
    batchSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(20)
      .default(5)
      .optional()
      .openapi({ description: 'Number of chunks to translate per LLM batch call (1-20, default: 5)', example: 5 }),
    skipReviewed: z
      .boolean()
      .default(true)
      .optional()
      .openapi({ description: 'Skip chunks marked as reviewed or approved', example: true }),
    skipExactMatches: z
      .boolean()
      .default(true)
      .optional()
      .openapi({ description: 'Skip chunks that already have 100% Exact Matches from TM', example: true }),
    minFuzzyToTranslate: z.coerce
      .number()
      .int()
      .min(0)
      .max(100)
      .optional()
      .openapi({ description: 'If TM match score is >= this threshold, skip LLM translation', example: 85 }),
    temperature: z.coerce
      .number()
      .min(0)
      .max(2)
      .default(0.3)
      .optional()
      .openapi({ description: 'Sampling temperature for LLM translation', example: 0.3 }),
  })
  .openapi('AutoTranslateProjectRequest');

export type AutoTranslateProjectRequestDto = z.infer<typeof AutoTranslateProjectRequestSchema>;

export const AutoTranslateProjectResponseSchema = z
  .object({
    translationId: z.string().openapi({ example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    totalChunks: z.number().int().nonnegative().openapi({ description: 'Total chunks in project' }),
    translatedChunksCount: z.number().int().nonnegative().openapi({ description: 'Chunks translated in this operation' }),
    skippedChunksCount: z.number().int().nonnegative().openapi({ description: 'Chunks skipped due to review/exact match filters' }),
    batchesProcessed: z.number().int().nonnegative().openapi({ description: 'Number of LLM batch requests executed' }),
    creditsDeducted: z.number().int().nonnegative().openapi({ description: 'Atomic credits deducted from workspace wallet' }),
    provider: z.string().openapi({ example: 'openai' }),
    model: z.string().openapi({ example: 'gpt-4o-mini' }),
    progressPercent: z.number().min(0).max(100).openapi({ description: 'Updated overall translation progress percentage (0-100)' }),
  })
  .openapi('AutoTranslateProjectResponse');

export type AutoTranslateProjectResponseDto = z.infer<typeof AutoTranslateProjectResponseSchema>;

// ============================================================================
// TAG INTEGRITY VALIDATION DTOs (Task 8.3.2)
// ============================================================================

export const ValidateTagIntegrityRequestSchema = z
  .object({
    sourceText: z
      .string()
      .min(1)
      .openapi({ description: 'Original source sentence containing tags, markup, or variables', example: 'Welcome <b>{name}</b>!' }),
    targetText: z
      .string()
      .openapi({ description: 'Candidate translated sentence to validate for tag preservation', example: 'Chào mừng <b>{name}</b>!' }),
  })
  .openapi('ValidateTagIntegrityRequest');

export type ValidateTagIntegrityRequestDto = z.infer<typeof ValidateTagIntegrityRequestSchema>;

export const ValidateTagIntegrityResponseSchema = z
  .object({
    isValid: z.boolean().openapi({ description: 'True when 100% tags and variables are preserved and balanced' }),
    missingTokens: z.array(z.string()).openapi({ description: 'Tags or variables missing from candidate translation' }),
    hallucinatedTokens: z.array(z.string()).openapi({ description: 'Spurious or hallucinated tags detected in candidate translation' }),
    unbalancedHtmlTags: z.array(z.string()).openapi({ description: 'Unclosed or mismatched HTML opening/closing tags' }),
    warnings: z.array(z.string()).openapi({ description: 'Human-readable QA warnings' }),
  })
  .openapi('ValidateTagIntegrityResponse');

export type ValidateTagIntegrityResponseDto = z.infer<typeof ValidateTagIntegrityResponseSchema>;

// ============================================================================
// TRANSCREATION & TONE CONTROL DTOs (Task 8.3.3)
// ============================================================================

export const TranscreateSegmentRequestSchema = z
  .object({
    sourceText: z
      .string()
      .min(1)
      .openapi({ description: 'Source sentence or headline to transcreate', example: 'Supercharge your team productivity with AI agents.' }),
    sourceLang: LanguageCodeSchema.default('en').openapi({ description: 'Source language code', example: 'en' }),
    targetLang: LanguageCodeSchema.default('vi').openapi({ description: 'Target language code', example: 'vi' }),
    tone: z
      .nativeEnum(TranscreationTone)
      .or(z.string())
      .default(TranscreationTone.MARKETING)
      .optional()
      .openapi({ description: 'Desired creative register / tone of voice', example: 'marketing' }),
    domain: TranslationDomainSchema.optional().openapi({ description: 'Subject domain context', example: 'tech' }),
    variationsCount: z.coerce
      .number()
      .int()
      .min(1)
      .max(5)
      .default(3)
      .optional()
      .openapi({ description: 'Number of creative variations to generate (1-5)', example: 3 }),
    brandVoiceId: z.string().optional().openapi({ description: 'Optional workspace brand voice profile ID', example: 'bv_profile_01' }),
    temperature: z.coerce
      .number()
      .min(0)
      .max(2)
      .default(0.7)
      .optional()
      .openapi({ description: 'Sampling temperature for creative transcreation', example: 0.7 }),
  })
  .openapi('TranscreateSegmentRequest');

export type TranscreateSegmentRequestDto = z.infer<typeof TranscreateSegmentRequestSchema>;

export const TranscreationVariationSchema = z
  .object({
    text: z.string().openapi({ description: 'Creative transcreated variation text' }),
    rationale: z.string().optional().openapi({ description: 'Copywriting rationale and cultural adaptation context' }),
    tone: z.string().optional().openapi({ description: 'Tone of voice reflected in this variation' }),
  })
  .openapi('TranscreationVariation');

export const TranscreateSegmentResponseSchema = z
  .object({
    sourceText: z.string().openapi({ example: 'Supercharge your team productivity with AI agents.' }),
    tone: z.string().openapi({ example: 'marketing' }),
    domain: z.string().optional().openapi({ example: 'tech' }),
    sourceLang: z.string().openapi({ example: 'en' }),
    targetLang: z.string().openapi({ example: 'vi' }),
    variations: z.array(TranscreationVariationSchema).openapi({ description: 'List of creative transcreated alternatives' }),
    creditsDeducted: z.number().int().nonnegative().openapi({ description: 'Credits deducted for transcreation generation' }),
  })
  .openapi('TranscreateSegmentResponse');

export type TranscreateSegmentResponseDto = z.infer<typeof TranscreateSegmentResponseSchema>;

// ============================================================================
// CHUNK REVIEW & EDITING WORKFLOW DTOs (Task 8.4.1)
// ============================================================================

export const TmSuggestionSchema = z
  .object({
    id: z.string().openapi({ example: 'tm_c8a91b2c' }),
    sourceText: z.string().openapi({ example: 'Welcome to our platform.' }),
    targetText: z.string().openapi({ example: 'Chào mừng bạn đến với nền tảng của chúng tôi.' }),
    similarityScore: z.number().int().min(0).max(100).openapi({ example: 92 }),
    matchType: z.string().openapi({ example: 'fuzzy' }),
  })
  .openapi('TmSuggestion');

export type TmSuggestionDto = z.infer<typeof TmSuggestionSchema>;

export const ChunkDetailResponseSchema = z
  .object({
    chunk: ChunkItemSchema,
    project: z.object({
      id: z.string(),
      title: z.string(),
      sourceLang: z.string(),
      targetLang: z.string(),
      domain: z.string(),
      tone: z.string().nullable(),
    }),
    tmSuggestions: z.array(TmSuggestionSchema).default([]).openapi({ description: 'Relevant fuzzy matches from Translation Memory' }),
    qaWarnings: z.array(z.string()).default([]).openapi({ description: 'QA warnings for current target translation' }),
  })
  .openapi('ChunkDetailResponse');

export type ChunkDetailResponseDto = z.infer<typeof ChunkDetailResponseSchema>;

export const UpdateChunkSchema = z
  .object({
    targetText: z.string().openapi({ description: 'Edited translated text', example: 'Bản dịch hoàn thiện' }),
    isReviewed: z.boolean().default(true).optional().openapi({ description: 'Mark segment as reviewed and verified' }),
    status: z.enum(['draft', 'reviewed']).default('reviewed').optional().openapi({ description: 'Updated review status' }),
    autoPropagate: z.boolean().default(false).optional().openapi({
      description: 'Automatically propagate this approved translation to all identical segments in the project',
      example: true,
    }),
    overwriteReviewed: z.boolean().default(false).optional().openapi({
      description: 'Whether to overwrite segments that are already reviewed when auto-propagating',
      example: false,
    }),
  })
  .openapi('UpdateChunk');

export type UpdateChunkDto = z.infer<typeof UpdateChunkSchema>;

export const BatchReviewChunksSchema = z
  .object({
    chunkIds: z.array(z.string()).min(1).max(500).openapi({ description: 'List of chunk IDs to update' }),
    action: z.enum(['approve', 'reject', 'reset']).default('approve').openapi({
      description: 'Review action: approve sets reviewed/approved, reject/reset reverts to draft/untranslated',
      example: 'approve',
    }),
    autoPropagate: z.boolean().default(false).optional().openapi({
      description: 'Automatically propagate approved translations to identical segments in the project',
      example: true,
    }),
    overwriteReviewed: z.boolean().default(false).optional().openapi({
      description: 'Whether to overwrite segments that are already reviewed when auto-propagating',
      example: false,
    }),
  })
  .openapi('BatchReviewChunks');

export type BatchReviewChunksDto = z.infer<typeof BatchReviewChunksSchema>;

export const BatchReviewChunksResponseSchema = z
  .object({
    updatedCount: z.number().int().nonnegative().openapi({ example: 10 }),
    action: z.string().openapi({ example: 'approve' }),
    affectedIds: z.array(z.string()).openapi({ description: 'IDs of successfully modified chunks' }),
    propagatedCount: z.number().int().nonnegative().optional().openapi({ description: 'Number of identical segments auto-propagated' }),
  })
  .openapi('BatchReviewChunksResponse');

export type BatchReviewChunksResponseDto = z.infer<typeof BatchReviewChunksResponseSchema>;

// ============================================================================
// AUTO-PROPAGATION PROJECT-WIDE DTOs (Task 8.4.2)
// ============================================================================

export const PropagateProjectRequestSchema = z
  .object({
    overwriteReviewed: z
      .boolean()
      .default(false)
      .optional()
      .openapi({ description: 'Whether to overwrite segments that are already reviewed/approved', example: false }),
  })
  .openapi('PropagateProjectRequest');

export type PropagateProjectRequestDto = z.infer<typeof PropagateProjectRequestSchema>;

export const PropagateProjectResponseSchema = z
  .object({
    translationId: z.string().openapi({ example: 'b0d6f0c4-0a3e-4d0a-9d4a-1d5f7c1a9a11' }),
    propagatedCount: z.number().int().nonnegative().openapi({ example: 4 }),
    affectedChunkIds: z.array(z.string()).openapi({ description: 'IDs of chunks that were updated by propagation' }),
  })
  .openapi('PropagateProjectResponse');

export type PropagateProjectResponseDto = z.infer<typeof PropagateProjectResponseSchema>;




